import * as React from 'react';
import { Link, Toggle, Select, TrustpilotSpinner, NotificationAlert, NotificationType, Box, Accordion } from '../CommonUI';
import Button from '../CommonUI/Control/Button';
import { TierFeature, Settings, BusinessUser, InvitationsSettings, EmailImageType, EmailSentFrom, EmailType } from '../Model';
import { consentIsAccepted } from '../Api/businessConsentsClient';
import { Auth } from '@trustpilot/ecommscript-integrationappauth';
import { Text as T } from 'ilenia';
import { Flex } from '../CommonUI/Layout';
import { getBusinessUnitTierFeatures } from '../Api/tiersClient';
import { integrationConfig, getIntegrationConfig } from '../Api/configapiClient';
import { getInvitationSettings } from '../Api/invitationSettingsClient';
import { css, StyleSheet } from 'aphrodite';
import SkuScanner from './SkuScanner';
import { EmailExample } from '../Components';

const upgradeUrl = process.env.REACT_APP_UpgradeTierUrl;

interface ProductReviewsProps {
    source: string;
    gtin?: string;
    mpn?: string;
    sku?: string;
    integrationKey: string;
    onLoggedInChange: (toggle: boolean) => void;
    onProductReviewsSettingChange: (selector: Settings) => void;
    onSettingsSave: () => void;
    productIdentificationOptions?: string[];
    sendPostMessage: (message: string) => void;
    tierName: string;
    businessUser: BusinessUser;
    shop?: string;
    onConsentChange: (toggle: boolean) => void;
    onToggleSettingsSave: (value: boolean) => void;
}

interface ProductReviewsState {
    enableProductReviews?: boolean;
    allowProductReviews: boolean;
    businessUnitId: string;
    saveError?: string;
    skuScannerIsOpen: boolean;
    currentEnableProductReviews?: boolean;
    invitationSettings?: InvitationsSettings;
}

export class ProductReviews extends React.Component<ProductReviewsProps, ProductReviewsState> {
    private mounted = true;

    constructor(props: ProductReviewsProps) {
        super(props);
        this.state = {
            enableProductReviews: false,
            currentEnableProductReviews: false,
            allowProductReviews: false,
            businessUnitId: '',
            skuScannerIsOpen: false,
            invitationSettings: undefined,
        };
        this.onProductReviewsToggle = this.onProductReviewsToggle.bind(this);
        this.onSave = this.onSave.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.onModalLinkClick = this.onModalLinkClick.bind(this);
        this.onModalClose = this.onModalClose.bind(this);
        this.skuIsNone = this.skuIsNone.bind(this);
    }

    public onSave() {
        const { businessUnitId, enableProductReviews } = this.state;
        this.props.onSettingsSave();
        this.props.onToggleSettingsSave(enableProductReviews || false);
        integrationConfig(businessUnitId, { enableProductReviews })
            .then((result) => {
                if (this.mounted) {
                    if (result) {
                        this.setState({saveError: 'Notifications.saveMessage', currentEnableProductReviews: enableProductReviews});
                    } else {
                        this.setState({saveError: 'invitations.error'});
                    }
                }
            });
    }

    public checkAuthAndConsent(integrationKey: string) {
        return Promise.all([
            Auth.isLoggedIn(process.env.REACT_APP_AuthenticationApiUrl!, process.env.REACT_APP_CookiesDomain!),
            consentIsAccepted(integrationKey)
        ]);
    }

    public componentDidMount() {
        analytics.page();
        const { integrationKey } = this.props;
        if (integrationKey) {
            this.checkAuthAndConsent(integrationKey).then((response) => {
                const [isLoggedIn, consentResponse] = response;
                if (isLoggedIn) {
                    if (consentResponse && consentResponse.consentIsAccepted) {
                        this.getProductReviewTabSettings(consentResponse.businessUnitId).then((settings) => {
                            const [features, config, invitationSettings] = settings;
                            if (features && features.length > 0 && config) {
                                if (this.mounted) {
                                    this.setState({
                                        enableProductReviews: config.enableProductReviews,
                                        currentEnableProductReviews: config.enableProductReviews,
                                        businessUnitId: consentResponse.businessUnitId,
                                        allowProductReviews: (features.indexOf(TierFeature.Reviews_ProductReviews) > -1),
                                        invitationSettings
                                    });
                                }
                            } else {
                                NotificationAlert.alert(NotificationType.Error, 'Notifications.loadDataError');
                            }
                        });
                    } else {
                        this.props.onConsentChange(false);
                    }
                } else {
                    this.props.onLoggedInChange(false);
                }
            });
        }
    }

    public componentWillUnmount() {
        this.mounted = false;
    }

    public getProductReviewTabSettings(businessUnitId: string) {
        return Promise.all([
            getBusinessUnitTierFeatures(businessUnitId),
            getIntegrationConfig(businessUnitId),
            getInvitationSettings(businessUnitId),
        ]);
    }

    public onSelectChange(type: string, value: string) {
        const newSettings = {};
        newSettings[type] = value;
        if (value && value !== 'none') {
            this.setState({enableProductReviews: true});
        }
        this.props.onProductReviewsSettingChange(newSettings as Settings);
    }

    public onChange(next: (event: any) => void) {
        return (event: any) => {
            return next(event);
        };
    }

    public onProductReviewsToggle(value: boolean) {
        const enableProductReviews = value;
        if (value && this.skuIsNone()) {
            this.onSelectChange('skuSelector', this.props.productIdentificationOptions![1]);
        }
        this.setState({ enableProductReviews });
    }

    public skuIsNone() {
        const { sku } = this.props;
        return !sku || sku === 'none';
    }

    public onModalLinkClick() {
        this.setState({skuScannerIsOpen: true});
    }

    public onModalClose() {
        this.setState({skuScannerIsOpen: false});
    }

    public render() {
        const { sku, gtin, mpn, productIdentificationOptions, sendPostMessage, source, tierName } = this.props;
        const { allowProductReviews, enableProductReviews, businessUnitId, skuScannerIsOpen, invitationSettings,
                currentEnableProductReviews } = this.state;
        if (!businessUnitId) {
            return <TrustpilotSpinner active={true} height="500px"/>;
        }
        const upgradeDescription = (
            <Link
                label="invitations.upgrade"
                href={upgradeUrl}
                pl={0}
                pr={0}
                mt={0}
                track={{
                    placement: 'Under enable product reviews',
                    target: 'Upgrade tier page in business app',
                    name: 'upgrade-tier-link',
                    source,
                    tierName,
                }}
            />
        );
        const modalLink = (
            <Box>
                <Link
                    label="ProductReviews.testInvitations"
                    pl={20}
                    pr={0}
                    mt={0}
                    onClick={this.onModalLinkClick}
                    track={{
                        placement: 'Under SKU selector',
                        target: 'Integration app SKU scanner',
                        name: 'check-all-skus',
                        source,
                        tierName,
                    }}
                />
            </Box>
        );
        const enableToStartDescription = <T id="ProductReviews.EnableToGetProductReviews" />;
        const enabledDescription = <T id="ProductReviews.ProductReviewsAreActive" />;
        const toggleNotSavedDescription = <div className={css(styles.greenText)}><T id="ProductReviews.ToggleNotSaved"/></div>;
        let toggleDescription;
        if (!allowProductReviews) {
            toggleDescription = upgradeDescription;
        } else if (enableProductReviews && currentEnableProductReviews !== enableProductReviews) {
            toggleDescription = toggleNotSavedDescription;
        } else if (enableProductReviews) {
            toggleDescription = enabledDescription;
        } else if (!enableProductReviews) {
            toggleDescription = enableToStartDescription;
        }

        const uniqueProductIdentificationOptions = [...new Set(productIdentificationOptions!)];
        return (
            <Box mt={0} pb={30} className={css(styles.layout)}>
                <Flex flexDirection="row" alignItems="flex-start" justifyContent="flex-start">
                    <Box className={css(styles.settingsBox)}>
                        <Flex>
                            {skuScannerIsOpen ? <SkuScanner skuSelector={sku!} source={source} sendPostMessage={sendPostMessage} tierName={tierName} onClose={this.onModalClose}/> : ''}
                            <Box mt={20} mb={20} pl={20} pr={40}>
                                <T id="ProductReviews.description" />
                            </Box>
                            <Toggle
                                id="toggle"
                                checked={enableProductReviews}
                                onChange={this.onChange(this.onProductReviewsToggle)}
                                disabled={!allowProductReviews && !currentEnableProductReviews}
                                label="invitations.enableProductReviews"
                                description={toggleDescription}
                                track={{
                                    placement: 'Product review tab form',
                                    action: 'Toggle product reviews',
                                    name: 'toggle-product-reviews',
                                    source: this.props.source,
                                    tierName,
                                }}
                            />
                            <Accordion
                                disabled={!allowProductReviews}
                                title="ProductReviews.AdvancedSettings"
                                data={[{
                                    id: 1,
                                    title: <T id="ProductReviews.SKUContainer" />,
                                    block: <div className={css(styles.optionalSeting)}>
                                        <p className={css(styles.accordionDescription)}>
                                            <T id="ProductReviews.SKUDescription" />
                                        </p>
                                        <Select
                                            id="skuSelector"
                                            name="ProductReviews.SKU"
                                            value={sku ? sku : 'none'}
                                            data={source === 'BigCommerce'
                                                ? uniqueProductIdentificationOptions.filter((option) => !['mpn', 'gtin'].includes(option))
                                                : uniqueProductIdentificationOptions
                                            }
                                            localize={true}
                                            disabled={!allowProductReviews}
                                            onChangeEvent={this.onSelectChange}
                                        />
                                        {!this.skuIsNone() ? modalLink : ''}
                                    </div>,
                                },
                                {
                                    id: 2,
                                    title: <T id="ProductReviews.GTINContainer" />,
                                    block: (<div className={css(styles.optionalSeting)}>
                                        <p className={css(styles.accordionDescription)}>
                                            <T id="ProductReviews.GTINDescription" />
                                        </p>
                                        <Select
                                            id="gtinSelector"
                                            name="ProductReviews.GTIN"
                                            value={gtin ? gtin : 'none'}
                                            data={uniqueProductIdentificationOptions}
                                            localize={true}
                                            disabled={!allowProductReviews}
                                            onChangeEvent={this.onSelectChange}
                                        />
                                    </div>)
                                },
                                {
                                    id: 3,
                                    title: <T id="ProductReviews.MPNContainer" />,
                                    block: (<div className={css(styles.optionalSeting)}>
                                        <p className={css(styles.accordionDescription)}>
                                            <T id="ProductReviews.MPNDescription" />
                                        </p>
                                        <Select
                                            id="mpnSelector"
                                            name="ProductReviews.MPN"
                                            value={mpn ? mpn : 'none'}
                                            data={uniqueProductIdentificationOptions}
                                            localize={true}
                                            disabled={!allowProductReviews}
                                            onChangeEvent={this.onSelectChange}
                                        />
                                    </div>)
                                }
                                ]}
                            />
                            <Button
                                onClick={this.onSave}
                                saveTextId={this.state.saveError}
                                disabled={!allowProductReviews && !currentEnableProductReviews}
                                track={{
                                    placement: 'Bottom left corner',
                                    action: 'Settings saved in invitations tab',
                                    name: 'invitations-save-settings-button',
                                    source: this.props.source,
                                    tierName,
                                }}
                            >
                                <T id="invitations.save" />
                            </Button>
                        </Flex>
                    </Box>
                    <EmailExample
                        invitationSettings={invitationSettings}
                        imageType={EmailImageType.ProductReviews}
                        sentFrom={EmailSentFrom.ProductReviews}
                        templateType={EmailType.ProductReviews}
                    />
                </Flex>
            </Box>
        );
    }
}

const styles = StyleSheet.create({
    layout: {
        maxWidth: '1160px',
    },
    settingsBox: {
        maxWidth: '602px',
        marginTop: '10px',
    },
    optionalSeting: {
        paddingBottom: '16px',
    },
    greenText: {
        color: 'rgb(0, 182, 122)',
    },
    accordionDescription: {
        padding: '0px 22px',
    },
});



// WEBPACK FOOTER //
// ./src/Containers/ProductReviews.tsx